import { useState, useEffect, useMemo, AnchorHTMLAttributes } from 'react';
import { applyGanalyticsTracking, urlIsExternal } from 'src/functions/propagateQueryParams';
import { safeNavigationHref } from 'src/functions/safeNavigationHref';
import { windowIsLoaded } from 'src/functions/windowIsLoaded';

const useBaseLink = (
  url: string,
  onClick?: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void
): AnchorHTMLAttributes<HTMLAnchorElement> => {
  const [queryPropagatedUrl, updateHref] = useState(url);

  useEffect(() => {
    windowIsLoaded().then(() => {
      updateHref(safeNavigationHref(url));
    });
  }, [url]);

  const onClickToScroll = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    onClick?.(event);

    event.preventDefault();
    document.querySelector(url)?.scrollIntoView({
      behavior: 'smooth',
    });
  };

  const handleClickWithTracking = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    onClick?.(event);
    applyGanalyticsTracking(queryPropagatedUrl)(event);
  };

  const isScrollEvent = url.includes('#');

  const targetProp = useMemo(() => {
    return urlIsExternal(url) ? { target: '_blank', rel: 'noreferrer' } : {};
  }, [url]);

  return {
    ...targetProp,
    href: queryPropagatedUrl,
    onClick: isScrollEvent ? onClickToScroll : handleClickWithTracking,
  };
};

export default useBaseLink;
